/**
 * The square that says what a row is — `design-system.md` §4.2.
 *
 * A tinted box with a coloured stroke glyph, at one of three sizes: 24 on a
 * line in a builder, 30 in a popup header, 40 for a visit. The colours are the
 * `--status-quoteLine-*` token pairs the rows already use, at a tenth alpha,
 * so a material is the same colour wherever it is drawn.
 *
 * `quote/quote-rows.tsx` had this as an inline `<span>` with a switch of raw
 * SVG beside it, which is why the popup header had no tile at all — there was
 * nothing to reuse. WP4 moves the rest of the builder in beside it and adds
 * the visit and service kinds.
 */
import type { ComponentType } from 'react'
import { cn } from '../lib/cn.js'
import { RECORD_ICONS, type RecordKind } from '../frame/icons.js'

/**
 * The five priced kinds, plus the two containers a catalog lists.
 *
 * A package, a service and a visit are not quote lines — they hold them —
 * but on a catalog row or in an editor's header they answer the same
 * question the tile answers everywhere else: what is this. They take the
 * accent rather than a `--status-quoteLine-*` pair, because they have no
 * kind colour of their own and borrowing one would make a package look like
 * a material.
 *
 * The four record kinds — contact, property, job, crew — are the headers of
 * the record flyouts (`record-flyouts.md` §2). Their colours are the
 * mockups' own: a contact is the info blue and a job is the accent
 * (`flyout-new-contact.html`, `flyout-new-job.html`).
 */
/*
 * Every kind that wears a tile. It is `RecordKind` rather than a list of its
 * own so a kind cannot be drawn without having a glyph, and a glyph cannot be
 * added without every tile being able to use it.
 */
export type TileKind = RecordKind

/**
 * The glyph for a kind, at whatever size the tile is.
 *
 * `RECORD_ICONS` — the same table the nav rail reads, so a contact wears the
 * same face in the rail, on the flyout that makes one, and on a lookup row.
 * These used to be hand-drawn here, and they had quietly drifted: the rail
 * drew two people for a contact and this drew one; the rail drew a map pin
 * for a property and this drew a house. Neither was wrong, which is exactly
 * why nobody noticed the same record had two faces.
 */
function Glyph({ kind, size, icon }: {
  kind: TileKind; size: number; icon?: ComponentType<{ size?: number; strokeWidth?: number }>
}) {
  const Icon = icon ?? RECORD_ICONS[kind]
  return <Icon size={size} strokeWidth={1.8} aria-hidden />
}

/**
 * The kinds that are not quote lines, and what colours them.
 *
 * Anything absent takes its `--status-quoteLine-*` pair, which is where the
 * five priced kinds live. Nothing here is a hex: a tile that introduced a
 * colour would be a colour the rest of the app does not have.
 */
const TINTS: Partial<Record<TileKind, string>> = {
  package: 'var(--color-accent)',
  service: 'var(--color-accent)',
  visit: 'var(--color-accent)',
  job: 'var(--color-accent)',
  contact: 'var(--color-info)',
  property: 'var(--status-quoteLine-deployment-fg)',
  crew: 'var(--status-quoteLine-equipment-fg)',

  /*
   * The organization, per `organization-build-book.md` §6: company ink,
   * region indigo, district teal, branch green, yard amber, zone purple. Four
   * levels of one tree need four tints a person can tell apart at 28px
   * without reading the word beside them.
   */
  company: 'var(--color-text)',
  region: 'var(--status-job-reviewed-fg)',
  district: 'var(--status-job-approved-fg)',
  branch: 'var(--color-accent)',
  yard: 'var(--status-quoteLine-sub-fg)',
  zone: 'var(--status-job-invoiced-fg)',
}

/** Glyph size by tile size, per §4.2: 13 in a 24, 15 in a 30–32, 19 in a 40–44. */
function glyphFor(size: number): number {
  if (size >= 40) return 19
  if (size >= 30) return 15
  return 13
}

/**
 * Radius by tile size, per §1.2: `tile-small` 7, `tile` 9, `tile-large` 11.
 *
 * The middle step starts at 26, not 30. Both mockups that draw a tile in a
 * tree row — the organization's at 28 and the scope switcher's at 26 — give
 * it a 9px radius, and the old threshold quietly rounded both of them at 7.
 */
function radiusFor(size: number): string {
  if (size >= 40) return 'rounded-tile-large'
  if (size >= 26) return 'rounded-tile'
  return 'rounded-tile-small'
}

export function KindTile({ kind, size = 24, className, icon, outlined }: {
  kind: TileKind
  size?: number
  className?: string
  icon?: ComponentType<{ size?: number; strokeWidth?: number }>
  /** A hairline in the tile's own ink — the editor header's 36px tile (Board 7). */
  outlined?: boolean
}) {
  const ink = TINTS[kind] ?? `var(--status-quoteLine-${kind}-fg)`
  return (
    <span
      aria-hidden
      className={cn('inline-flex shrink-0 items-center justify-center',
        // Board 7's outlined editor tile: 36 at a 10 radius, an 18 glyph.
        outlined ? 'rounded-[10px]' : radiusFor(size), className)}
      style={{
        width: size,
        height: size,
        background: `rgb(${ink} / 0.1)`,
        color: `rgb(${ink})`,
        ...(outlined ? { border: `1px solid rgb(${ink} / 0.35)` } : {}),
      }}
    >
      <Glyph kind={kind} size={outlined ? Math.round(size / 2) : glyphFor(size)} icon={icon} />
    </span>
  )
}
